import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Heart, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

export default function ReentrySupportPage() {
  return (
    <div>
      {/* Hero Section */}
      <section className="relative py-20 lg:py-28 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 right-20 w-72 h-72 bg-teal-500/20 rounded-full blur-3xl" />
          <div className="absolute bottom-10 left-20 w-64 h-64 bg-amber-500/20 rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl sm:text-5xl font-bold mb-6">Reentry Support</h1>
        </div>
      </section>

      {/* Home Plans & Reentry Preparation */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">Home Plans & Reentry Preparation</h2>
          <p className="text-lg text-slate-500 mb-12">(Incarcerated Individuals)</p>
          
          <h3 className="text-2xl font-bold text-slate-900 mb-6">Planning for Reentry Begins Before Release</h3>
          
          <p className="text-lg text-slate-600 mb-6">
            Planning for reentry should begin well before release. Many correctional systems, parole boards, and reentry programs require or strongly recommend that individuals develop a home plan and begin preparing for community transition while still incarcerated.
          </p>
          
          <p className="text-lg text-slate-600">
            Early planning helps reduce delays in release, supports housing stability, and improves long-term reentry outcomes.
          </p>
        </div>
      </section>

      {/* What Is a Home Plan? */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-8">What Is a Home Plan?</h2>
          
          <p className="text-lg text-slate-600 mb-6">
            A home plan is a written document that outlines where a person will live after release, who will support them, and how basic needs such as housing, supervision, and stability will be met.
          </p>
          
          <p className="text-lg text-slate-600 font-medium mb-6">
            In many states and parole systems, an approved home plan is required before release.
          </p>
          
          <p className="text-lg text-slate-600 mb-4">A home plan may include:</p>
          
          <ul className="space-y-3 text-lg text-slate-600 mb-8">
            <li className="flex items-start gap-3">
              <CheckCircle className="w-6 h-6 text-teal-600 flex-shrink-0 mt-0.5" />
              Proposed residential address or housing provider
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle className="w-6 h-6 text-teal-600 flex-shrink-0 mt-0.5" />
              Plan for housing support or sponsor
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle className="w-6 h-6 text-teal-600 flex-shrink-0 mt-0.5" />
              Employment, education, or income plans
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle className="w-6 h-6 text-teal-600 flex-shrink-0 mt-0.5" />
              Contact information for support persons
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle className="w-6 h-6 text-teal-600 flex-shrink-0 mt-0.5" />
              Agreements or letters confirming willingness to provide housing
            </li>
          </ul>
          
          <p className="text-lg text-slate-600 italic">
            Requirements vary by state, parole authority, and supervision conditions.
          </p>
        </div>
      </section>

      {/* Why Home Plans Matter */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-8">Why Home Plans Matter</h2>
          
          <p className="text-lg text-slate-600 mb-6">
            Identifying stable housing and planning for release is a critical component of successful reentry. Research consistently shows that housing stability is linked to:
          </p>
          
          <ul className="space-y-3 text-lg text-slate-600 mb-8">
            <li className="flex items-start gap-3">
              <CheckCircle className="w-6 h-6 text-teal-600 flex-shrink-0 mt-0.5" />
              Reduced recidivism
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle className="w-6 h-6 text-teal-600 flex-shrink-0 mt-0.5" />
              Increased access to employment and treatment
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle className="w-6 h-6 text-teal-600 flex-shrink-0 mt-0.5" />
              Improved compliance with supervision requirements
            </li>
          </ul>
          
          <p className="text-lg text-slate-600">
            A clear home plan helps individuals, families, and supervising agencies prepare for a safer and more stable transition.
          </p>
        </div>
      </section>

      {/* How Home Plans Are Used */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-8">How Home Plans Are Used</h2>
          
          <p className="text-lg text-slate-600 mb-6">
            In systems such as the Pennsylvania Department of Corrections:
          </p>
          
          <ul className="space-y-3 text-lg text-slate-600 mb-8">
            <li className="flex items-start gap-3">
              <CheckCircle className="w-6 h-6 text-teal-600 flex-shrink-0 mt-0.5" />
              Incarcerated individuals submit a proposed home plan to their parole agent prior to release
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle className="w-6 h-6 text-teal-600 flex-shrink-0 mt-0.5" />
              Parole staff may contact the proposed residence for verification
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle className="w-6 h-6 text-teal-600 flex-shrink-0 mt-0.5" />
              Approved home plans support compliance with supervision conditions
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle className="w-6 h-6 text-teal-600 flex-shrink-0 mt-0.5" />
              Interstate home plans (living in another state) require additional review and may involve application fees
            </li>
          </ul>
          
          <p className="text-lg text-slate-600 italic">
            Processes vary by jurisdiction.
          </p>
        </div>
      </section>

      {/* Preparing for Reentry While Incarcerated */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-8">Preparing for Reentry While Incarcerated</h2>
          
          <p className="text-lg text-slate-600 mb-6">
            In addition to developing a home plan, incarcerated individuals may begin reentry preparation by accessing:
          </p>
          
          <ul className="space-y-3 text-lg text-slate-600 mb-6">
            <li className="flex items-start gap-3">
              <CheckCircle className="w-6 h-6 text-teal-600 flex-shrink-0 mt-0.5" />
              Reentry planning guides that help map goals and next steps
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle className="w-6 h-6 text-teal-600 flex-shrink-0 mt-0.5" />
              In-institution programs, when available, that focus on:
            </li>
          </ul>
          
          <ul className="space-y-2 text-lg text-slate-600 pl-12 mb-8">
            <li>• Job readiness</li>
            <li>• Life skills</li>
            <li>• Financial literacy</li>
            <li>• Substance use planning</li>
            <li>• Family and community engagement</li>
          </ul>
          
          <p className="text-lg text-slate-600">
            Participation in prerelease programs that address housing, employment, recovery, and community resources has been shown to support better reintegration outcomes.
          </p>
        </div>
      </section>

      {/* Helpful Resources for Incarcerated Individuals */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-8">Helpful Resources for Incarcerated Individuals</h2>
          
          <p className="text-lg text-slate-600 mb-8">
            The following public and legitimate resources may help individuals prepare for release and build a reentry plan:
          </p>
          
          <div className="space-y-6 mb-8">
            <Card className="border-0 shadow-md">
              <CardContent className="p-6">
                <h3 className="font-bold text-lg text-slate-900 mb-2">National Reentry Resource Center</h3>
                <p className="text-slate-600">Federal tools and reentry guidance</p>
              </CardContent>
            </Card>
            
            <Card className="border-0 shadow-md">
              <CardContent className="p-6">
                <h3 className="font-bold text-lg text-slate-900 mb-2">Education Justice Project – Reentry Guides</h3>
                <p className="text-slate-600">Planning guides covering housing, recovery, employment, and education</p>
              </CardContent>
            </Card>
            
            <Card className="border-0 shadow-md">
              <CardContent className="p-6">
                <h3 className="font-bold text-lg text-slate-900 mb-2">Residential Reentry Centers (Halfway Houses)</h3>
                <p className="text-slate-600">Structured transitional environments supporting stability and employment</p>
              </CardContent>
            </Card>
          </div>
          
          <p className="text-lg text-slate-600 italic">
            Availability of prerelease programs and home plan requirements varies by institution, jurisdiction, and state.
          </p>
        </div>
      </section>

      {/* Step-by-Step Reentry Checklist */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">Step-by-Step Reentry Checklist</h2>
          <p className="text-lg text-slate-500 mb-12">(For Incarcerated Individuals & Families)</p>
          
          {/* Before Release */}
          <div className="mb-12">
            <h3 className="text-2xl font-bold text-slate-900 mb-6 pb-2 border-b-2 border-teal-500">Before Release</h3>
            <ol className="space-y-4 text-lg text-slate-600">
              <li className="flex items-start gap-4">
                <span className="w-8 h-8 bg-teal-100 text-teal-700 rounded-full flex items-center justify-center font-bold flex-shrink-0">1</span>
                Identify potential housing options (family, transitional housing, reentry programs)
              </li>
              <li className="flex items-start gap-4">
                <span className="w-8 h-8 bg-teal-100 text-teal-700 rounded-full flex items-center justify-center font-bold flex-shrink-0">2</span>
                Gather required documents (ID, Social Security card, birth certificate)
              </li>
              <li className="flex items-start gap-4">
                <span className="w-8 h-8 bg-teal-100 text-teal-700 rounded-full flex items-center justify-center font-bold flex-shrink-0">3</span>
                Identify recovery or treatment support if needed
              </li>
              <li className="flex items-start gap-4">
                <span className="w-8 h-8 bg-teal-100 text-teal-700 rounded-full flex items-center justify-center font-bold flex-shrink-0">4</span>
                Develop an employment, education, or training plan
              </li>
              <li className="flex items-start gap-4">
                <span className="w-8 h-8 bg-teal-100 text-teal-700 rounded-full flex items-center justify-center font-bold flex-shrink-0">5</span>
                Understand parole or supervision requirements
              </li>
            </ol>
          </div>
          
          {/* At Release */}
          <div className="mb-12">
            <h3 className="text-2xl font-bold text-slate-900 mb-6 pb-2 border-b-2 border-amber-500">At Release</h3>
            <ol className="space-y-4 text-lg text-slate-600" start="6">
              <li className="flex items-start gap-4">
                <span className="w-8 h-8 bg-amber-100 text-amber-700 rounded-full flex items-center justify-center font-bold flex-shrink-0">6</span>
                Confirm transportation from facility
              </li>
              <li className="flex items-start gap-4">
                <span className="w-8 h-8 bg-amber-100 text-amber-700 rounded-full flex items-center justify-center font-bold flex-shrink-0">7</span>
                Confirm housing placement or temporary stay
              </li>
              <li className="flex items-start gap-4">
                <span className="w-8 h-8 bg-amber-100 text-amber-700 rounded-full flex items-center justify-center font-bold flex-shrink-0">8</span>
                Connect with supervision officer or case manager
              </li>
            </ol>
          </div>
          
          {/* After Release */}
          <div>
            <h3 className="text-2xl font-bold text-slate-900 mb-6 pb-2 border-b-2 border-teal-500">After Release</h3>
            <ol className="space-y-4 text-lg text-slate-600" start="9">
              <li className="flex items-start gap-4">
                <span className="w-8 h-8 bg-teal-100 text-teal-700 rounded-full flex items-center justify-center font-bold flex-shrink-0">9</span>
                Attend required appointments (parole, treatment, workforce)
              </li>
              <li className="flex items-start gap-4">
                <span className="w-8 h-8 bg-teal-100 text-teal-700 rounded-full flex items-center justify-center font-bold flex-shrink-0">10</span>
                Continue recovery and reentry support
              </li>
              <li className="flex items-start gap-4">
                <span className="w-8 h-8 bg-teal-100 text-teal-700 rounded-full flex items-center justify-center font-bold flex-shrink-0">11</span>
                Adjust housing or employment plans as needed
              </li>
            </ol>
          </div>
        </div>
      </section>

      {/* Important Notes */}
      <section className="py-20 bg-amber-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-start gap-4 mb-8">
            <AlertCircle className="w-8 h-8 text-amber-600 flex-shrink-0" />
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900">Important Notes</h2>
          </div>
          
          <ul className="space-y-3 text-lg text-slate-600 mb-12">
            <li>• Availability and eligibility vary by state and program</li>
            <li>• POOSF does not guarantee services, placement, or outcomes</li>
            <li>• All organizations listed operate independently</li>
          </ul>
          
          <div className="border-t border-amber-200 pt-8">
            <h3 className="text-2xl font-bold text-slate-900 mb-6">Resource Disclaimer</h3>
            <p className="text-lg text-slate-600">
              The Productive Offenders of Society Foundation provides public and legitimate information for community awareness and resource navigation. POOSF does not operate, endorse, or guarantee services provided by third-party organizations. Inclusion on this website does not constitute affiliation or partnership unless explicitly stated.
            </p>
          </div>
        </div>
      </section>

      {/* Our Commitment */}
      <section className="py-20 bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold mb-8">Our Commitment</h2>
          
          <p className="text-xl text-slate-300 mb-8">
            At POOSF, we believe reentry and recovery are not moral failures — they are community responsibilities.
          </p>
          
          <p className="text-lg text-slate-300 mb-4">We are committed to:</p>
          
          <ul className="space-y-3 text-lg text-slate-300">
            <li className="flex items-start gap-3">
              <CheckCircle className="w-6 h-6 text-teal-400 flex-shrink-0 mt-0.5" />
              Ending stigma
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle className="w-6 h-6 text-teal-400 flex-shrink-0 mt-0.5" />
              Supporting informed access to help
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle className="w-6 h-6 text-teal-400 flex-shrink-0 mt-0.5" />
              Strengthening communities through connection, education, and support
            </li>
          </ul>
        </div>
      </section>

      {/* This Resource Navigation Model */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-8">This Resource Navigation Model Is Used Nationwide</h2>
          
          <p className="text-lg text-slate-600 mb-6">
            Many established public and nonprofit systems use resource navigation to connect individuals and families to support, including:
          </p>
          
          <ul className="space-y-3 text-lg text-slate-600 mb-8">
            <li className="flex items-start gap-3">
              <CheckCircle className="w-6 h-6 text-teal-600 flex-shrink-0 mt-0.5" />
              United Way 211 (available in all states)
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle className="w-6 h-6 text-teal-600 flex-shrink-0 mt-0.5" />
              SAMHSA Treatment Locator (national)
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle className="w-6 h-6 text-teal-600 flex-shrink-0 mt-0.5" />
              Legal Aid directories (national and state-based)
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle className="w-6 h-6 text-teal-600 flex-shrink-0 mt-0.5" />
              State and local reentry councils
            </li>
          </ul>
          
          <p className="text-lg text-slate-600">
            POOSF follows this same public-service navigation model to help individuals identify available resources and take informed next steps.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-teal-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            View the Resource Directory
          </h2>
          <p className="text-xl text-teal-100 mb-10">
            Access links to national resources for housing, reentry support, healthcare, and more.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button asChild size="lg" className="bg-white text-teal-700 hover:bg-teal-50">
              <Link to="/resources">
                Resource Directory
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </Button>
            <Button asChild size="lg" className="bg-amber-500 hover:bg-amber-400 text-white">
              <Link to="/contact">
                Contact Us
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
